import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useData } from '../context/DataContext';

const { FiCar, FiMessageSquare, FiEye, FiPlus, FiTrendingUp, FiUsers, FiDollarSign, FiActivity } = FiIcons;

const AdminDashboard = () => {
  const { vehicles, inquiries } = useData();

  const activeVehicles = vehicles.filter(v => v.status === 'active');
  const pendingInquiries = inquiries.filter(i => i.status === 'pending');
  const totalValue = activeVehicles.reduce((sum, v) => sum + v.price, 0);

  const stats = [
    { name: 'Vehículos Activos', value: activeVehicles.length, icon: FiCar, color: 'bg-[#3498db]', link: '/admin/vehiculos' },
    { name: 'Consultas Pendientes', value: pendingInquiries.length, icon: FiMessageSquare, color: 'bg-[#e67e22]', link: '/admin/consultas' },
    { name: 'Valor Total Stock', value: `€${totalValue.toLocaleString()}`, icon: FiDollarSign, color: 'bg-[#27ae60]', link: '/admin/vehiculos' },
    { name: 'Total Vehículos', value: vehicles.length, icon: FiActivity, color: 'bg-[#9b59b6]', link: '/admin/vehiculos' }
  ];

  const recentVehicles = vehicles
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 5);

  const recentInquiries = inquiries
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 5);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0
    }).format(price);
  };

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#2c3e50]">Panel de Control</h1>
        <p className="text-gray-600 mt-2">Resumen general de AUTOSFERSAN</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
          >
            <Link to={stat.link} className="block">
              <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                    <p className="text-2xl font-bold text-[#2c3e50] mt-2">{stat.value}</p>
                  </div>
                  <div className={`p-3 rounded-full ${stat.color}`}>
                    <SafeIcon icon={stat.icon} className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Vehicles */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-lg shadow-md"
        >
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-[#2c3e50]">Vehículos Recientes</h2>
              <Link
                to="/admin/vehiculos/nuevo"
                className="bg-[#e67e22] hover:bg-orange-600 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
              >
                <SafeIcon icon={FiPlus} className="w-4 h-4" />
                <span>Añadir</span>
              </Link>
            </div>
          </div>
          <div className="p-6">
            {recentVehicles.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No hay vehículos registrados</p>
            ) : (
              <div className="space-y-4">
                {recentVehicles.map((vehicle) => (
                  <div key={vehicle.id} className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                    <img
                      src={vehicle.images[0] || 'https://images.unsplash.com/photo-1555215695-3004980ad54e?w=80&h=60&fit=crop'}
                      alt={`${vehicle.brand} ${vehicle.model}`}
                      className="w-16 h-12 object-cover rounded"
                    />
                    <div className="flex-1">
                      <h3 className="font-semibold text-[#2c3e50]">
                        {vehicle.brand} {vehicle.model}
                      </h3>
                      <p className="text-sm text-gray-600">{vehicle.version} - {vehicle.year}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-[#27ae60]">{formatPrice(vehicle.price)}</p>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        vehicle.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {vehicle.status === 'active' ? 'Activo' : 'Inactivo'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-4 text-center">
              <Link to="/admin/vehiculos" className="text-[#005c91] hover:text-[#3498db] font-medium">
                Ver todos los vehículos →
              </Link>
            </div>
          </div>
        </motion.div>

        {/* Recent Inquiries */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-lg shadow-md"
        >
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-bold text-[#2c3e50]">Consultas Recientes</h2>
          </div>
          <div className="p-6">
            {recentInquiries.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No hay consultas registradas</p>
            ) : (
              <div className="space-y-4">
                {recentInquiries.map((inquiry) => (
                  <div key={inquiry.id} className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-semibold text-[#2c3e50]">{inquiry.name}</h3>
                        <p className="text-sm text-gray-600">{inquiry.vehicleName}</p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        inquiry.status === 'pending' ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {inquiry.status === 'pending' ? 'Pendiente' : 'Respondido'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">
                      {inquiry.message.length > 100 ? `${inquiry.message.substring(0, 100)}...` : inquiry.message}
                    </p>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>{inquiry.email}</span>
                      <span>{new Date(inquiry.created_at).toLocaleDateString('es-ES')}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-4 text-center">
              <Link to="/admin/consultas" className="text-[#005c91] hover:text-[#3498db] font-medium">
                Ver todas las consultas →
              </Link>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        className="mt-8 bg-white rounded-lg shadow-md p-6"
      >
        <h2 className="text-xl font-bold text-[#2c3e50] mb-6">Acciones Rápidas</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link
            to="/admin/vehiculos/nuevo"
            className="flex items-center space-x-3 p-4 bg-[#3498db] hover:bg-blue-600 text-white rounded-lg transition-colors"
          >
            <SafeIcon icon={FiPlus} className="w-6 h-6" />
            <span className="font-medium">Añadir Vehículo</span>
          </Link>
          <Link
            to="/admin/vehiculos"
            className="flex items-center space-x-3 p-4 bg-[#27ae60] hover:bg-green-600 text-white rounded-lg transition-colors"
          >
            <SafeIcon icon={FiEye} className="w-6 h-6" />
            <span className="font-medium">Gestionar Vehículos</span>
          </Link>
          <Link
            to="/admin/consultas"
            className="flex items-center space-x-3 p-4 bg-[#e67e22] hover:bg-orange-600 text-white rounded-lg transition-colors"
          >
            <SafeIcon icon={FiMessageSquare} className="w-6 h-6" />
            <span className="font-medium">Ver Consultas</span>
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminDashboard;